import React from "react";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function FAQSection() {
  const faqs = [
    {
      question: "What is JobPool.ai, and how is it different from job boards like Indeed, LinkedIn or Glassdoor?",
      answer: "JobPool.ai is an AI-powered platform that delivers personalized job matches. Instead of manually searching, you upload your resume and preferences, and our AI semantically ranks real-time job listings based on your skills, experience, and goals — not just keywords. You'll receive a curated report on the website and also via email in minutes.",
    },
    {
      question: "How does JobPool.ai match me with jobs?",
      answer: "You upload a one-page PDF resume (up to 1MB) and optionally add job preferences. Our AI, using advanced language models, analyzes your profile and ranks fresh jobs based on semantic alignment, ensuring high-precision matches.",
    },
    {
      question: "How long does it take to receive my job matches?",
      answer: "You’ll typically receive your personalized job report within minutes of uploading your resume and preferences. Our system prioritizes instant delivery to help you apply faster.",
    },
    {
      question: "How accurate are the job matches?",
      answer: "With detailed resumes and clear preferences, our AI achieves up to 95–99% matching accuracy, providing highly relevant opportunities tailored to your profile.",
    },
    {
      question: "Is my personal information safe with JobPool.ai?",
      answer: "Absolutely. All data is secured using 256-bit encryption. Your information is never sold or shared with third parties, and we comply fully with GDPR and CCPA standards.",
    },
    {
      question: "What happens to my data after receiving my job report?",
      answer: "Your data remains securely stored and is never shared or sold. You may also request full deletion at any time by contacting our support team.",
    },
    {
      question: "What’s the difference between the Free and paid plans?",
      answer: "Paid plans offer more curated matches and deeper AI analysis for even higher accuracy.",
    },
    {
      question: "Can I get a refund if I'm not satisfied?",
      answer: "Because each job report is generated personally for you using real-time AI analysis — a process that incurs significant operational costs — we’re unable to offer refunds once a report is created.\n" +
          "To ensure you can experience the platform risk-free, we offer a Free plan that delivers 5 curated job matches.\n" +
          "If you encounter any issues or need assistance, our support team is always happy to help at support@jobpool.ai.",
    },
    {
      question: "Does JobPool.ai guarantee I’ll find a job?",
      answer: "While we deliver highly accurate matches, hiring decisions ultimately depend on employers. JobPool.ai helps you find the right opportunities faster, but success also depends on your application and interviews.",
    },
    {
      question: "What affects the quality of my job matches?",
      answer: "Match quality depends on how clearly you define your skills, experience, and preferences. The more specific and detailed your input, the better the results.",
    },
  ];

  return (
      <div className="py-16 md:py-24">
        <div className="text-center mb-16">
          <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="px-4 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full inline-block mb-4"
          >
            FAQ
          </motion.span>
          <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold text-gray-900"
          >
            Frequently Asked Questions
          </motion.h2>
          <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
              className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto"
          >
            Everything you need to know about how JobPool.ai works
          </motion.p>
        </div>

        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
                <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                >
                  <AccordionItem value={`item-${index}`} className="border border-gray-200 mb-4 rounded-lg overflow-hidden">
                    <AccordionTrigger className="text-left px-6 py-4 hover:bg-gray-50">
                      <span className="font-medium text-gray-900">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 px-6 pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
            ))}
          </Accordion>
        </div>
      </div>
  );
}